import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { callApi, HttpMethod } from "src/services/apiService";
import {
  Grid,
  GridColumn as Column,
  GridCellProps,
} from "@progress/kendo-react-grid";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import ReactDOM from "react-dom";

interface AiSession {
  id: number;
  anonId: string | null;
  userId: number | null;
  threadId: string | null;
  threadName: string;
  messageCount: number;
  createdOn: string;
  lastModifiedOn: string;
  email: string;
  fromIP: string;
  isDeleted: boolean;
  cloudflarePassed: boolean | null;
  acctId: string | null;
  userDisplay: string | null;
}

interface AiInteraction {
  id: number;
  aiUsageId: number;
  interactionId: string | null;
  userMessage: string;
  aiResponse: string;
  responseTime: number | null;
  createdOn: string;
  isLiked: boolean | null;
  isReported: boolean | null;
  reportMessage: string | null;
  promptTokens: number | null;
  completionTokens: number | null;
  totalTokens: number | null;
  isPublic: boolean;
  acctId: string | null;
  isSpeech: boolean;
}

// Helper function to detect if IP is from a developer (local/private network)
const isDevIP = (ip: string | null): boolean => {
  if (!ip) return false;
  return (
    ip.startsWith("192.168.") ||
    ip.startsWith("10.") ||
    ip.startsWith("172.16.") ||
    ip.startsWith("172.17.") ||
    ip.startsWith("172.18.") ||
    ip.startsWith("172.19.") ||
    ip.startsWith("172.20.") ||
    ip.startsWith("172.21.") ||
    ip.startsWith("172.22.") ||
    ip.startsWith("172.23.") ||
    ip.startsWith("172.24.") ||
    ip.startsWith("172.25.") ||
    ip.startsWith("172.26.") ||
    ip.startsWith("172.27.") ||
    ip.startsWith("172.28.") ||
    ip.startsWith("172.29.") ||
    ip.startsWith("172.30.") ||
    ip.startsWith("172.31.") ||
    ip === "127.0.0.1" ||
    ip === "::1" ||
    ip === "localhost"
  );
};

const loadingPanelMarkup = (
  <div className="k-loading-mask">
    <span className="k-loading-text">Loading</span>
    <div className="k-loading-image" />
    <div className="k-loading-color" />
  </div>
);

const LoadingPanel = (props: { gridRef: React.RefObject<HTMLDivElement> }) => {
  const { gridRef } = props;
  const gridContent =
    gridRef.current && gridRef.current.querySelector(".k-grid-content");
  return gridContent
    ? ReactDOM.createPortal(loadingPanelMarkup, gridContent)
    : loadingPanelMarkup;
};

const AIInteractions: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<AiSession | null>(null);
  const [interactions, setInteractions] = useState<AiInteraction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedInteraction, setSelectedInteraction] =
    useState<AiInteraction | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);

  const loadData = async () => {
    if (!id) return;

    setIsLoading(true);
    setError("");
    try {
      // Load session details
      const sessionResponse = await callApi(
        `sysadmin/ai/sessions/${id}`,
        HttpMethod.GET,
      );
      setSession(sessionResponse);

      // Load interactions
      const interactionsResponse = await callApi(
        `sysadmin/ai/sessions/${id}/interactions`,
        HttpMethod.GET,
      );
      setInteractions(interactionsResponse.data || []);
    } catch (err: any) {
      console.error("Error fetching session data:", err);
      setError(`Error fetching session data: ${err.message || err}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const handleBack = () => {
    navigate("/sysadmin/ai/sessions");
  };

  const handleRowClick = (dataItem: AiInteraction) => {
    setSelectedInteraction(dataItem);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setSelectedInteraction(null);
  };

  // Truncate text for grid display
  const truncateText = (text: string, maxLength: number = 100): string => {
    if (!text) return "-";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Custom cell for clickable text with truncation
  const TruncatedCell: React.FC<GridCellProps & { maxLength?: number }> = (
    props,
  ) => {
    const { dataItem, field } = props;
    const value = field ? dataItem[field] : "";
    const isReported = dataItem.isReported === true;
    return (
      <td
        onClick={() => handleRowClick(dataItem)}
        style={{
          cursor: "pointer",
          backgroundColor: isReported ? "#ffebee" : undefined,
        }}
      >
        {truncateText(value, 100)}
      </td>
    );
  };

  // Custom cell for date formatting
  const DateCell: React.FC<GridCellProps> = (props) => {
    const { dataItem, field } = props;
    const value = field ? dataItem[field] : null;
    const formatted = value
      ? new Date(value).toLocaleString("en-US", {
          dateStyle: "short",
          timeStyle: "short",
        })
      : "-";
    const isReported = dataItem.isReported === true;
    return (
      <td
        onClick={() => handleRowClick(dataItem)}
        style={{
          cursor: "pointer",
          backgroundColor: isReported ? "#ffebee" : undefined,
        }}
      >
        {formatted}
      </td>
    );
  };

  // Custom cell for numeric values
  const NumericCell: React.FC<GridCellProps> = (props) => {
    const { dataItem, field } = props;
    const value = field ? dataItem[field] : null;
    const isReported = dataItem.isReported === true;
    return (
      <td
        onClick={() => handleRowClick(dataItem)}
        style={{
          cursor: "pointer",
          backgroundColor: isReported ? "#ffebee" : undefined,
        }}
      >
        {value !== null && value !== undefined ? value.toLocaleString() : "-"}
      </td>
    );
  };

  // Custom cell for boolean display with icons
  const FeedbackCell: React.FC<GridCellProps> = (props) => {
    const { dataItem, field } = props;
    const value = field ? dataItem[field] : null;
    const isReported = dataItem.isReported === true;

    let display = "-";
    let color = undefined;
    if (field === "isLiked") {
      if (value === true) {
        display = "👍";
      } else if (value === false) {
        display = "👎";
      }
    } else if (field === "isReported") {
      if (value === true) {
        display = "⚠️ Yes";
        color = "#c62828";
      } else {
        display = "-";
      }
    }

    return (
      <td
        onClick={() => handleRowClick(dataItem)}
        style={{
          cursor: "pointer",
          backgroundColor: isReported ? "#ffebee" : undefined,
          color: color,
          fontWeight:
            value === true && field === "isReported" ? "bold" : undefined,
        }}
      >
        {display}
      </td>
    );
  };

  // Custom cell for Source (Public/Private/Developer)
  const SourceCell: React.FC<GridCellProps> = (props) => {
    const { dataItem } = props;
    const isReported = dataItem.isReported === true;
    const isDev = session && isDevIP(session.fromIP);

    let badge = "";
    let badgeClass = "";
    let extraInfo = "";

    if (isDev) {
      badge = "Developer";
      badgeClass = "bg-warning text-dark";
    } else if (dataItem.isPublic) {
      badge = "Public";
      badgeClass = "bg-info";
    } else {
      badge = "Private";
      badgeClass = "bg-primary";
      // Use session acctId since it's derived from userId
      if (session?.acctId) {
        extraInfo = session.acctId;
      }
    }

    return (
      <td
        onClick={() => handleRowClick(dataItem)}
        style={{
          cursor: "pointer",
          backgroundColor: isReported ? "#ffebee" : undefined,
        }}
      >
        <span className={`badge ${badgeClass}`}>{badge}</span>
        {extraInfo && <small className="ms-1 text-muted">{extraInfo}</small>}
      </td>
    );
  };

  // Custom cell for Mode (Speech/Text)
  const ModeCell: React.FC<GridCellProps> = (props) => {
    const { dataItem } = props;
    const isReported = dataItem.isReported === true;

    return (
      <td
        onClick={() => handleRowClick(dataItem)}
        style={{
          cursor: "pointer",
          backgroundColor: isReported ? "#ffebee" : undefined,
        }}
      >
        {dataItem.isSpeech ? "🎤 Voice" : "💬 Text"}
      </td>
    );
  };

  return (
    <div className="container-fluid p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Session Interactions</h4>
        <button className="btn btn-secondary" onClick={handleBack}>
          ← Back to Sessions
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Session Info Card */}
      {session && (
        <div className="card mb-3">
          <div className="card-header">
            <strong>Session #{session.id}</strong>
            {session.isDeleted && (
              <span className="badge bg-danger ms-2">Deleted</span>
            )}
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-3">
                <strong>Email:</strong> {session.email || "-"}
              </div>
              <div className="col-md-2">
                <strong>IP:</strong> {session.fromIP || "-"}
              </div>
              <div className="col-md-3">
                <strong>Thread:</strong> {session.threadName || "-"}
              </div>
              <div className="col-md-2">
                <strong>Messages:</strong> {session.messageCount}
              </div>
              <div className="col-md-2">
                <strong>CF Passed:</strong>{" "}
                {session.cloudflarePassed === true
                  ? "Yes"
                  : session.cloudflarePassed === false
                    ? "No"
                    : "-"}
              </div>
            </div>
            <div className="row mt-2">
              <div className="col-md-3">
                <strong>Created:</strong>{" "}
                {new Date(session.createdOn).toLocaleString()}
              </div>
              <div className="col-md-3">
                <strong>Last Modified:</strong>{" "}
                {new Date(session.lastModifiedOn).toLocaleString()}
              </div>
              <div className="col-md-3">
                <strong>Thread ID:</strong>{" "}
                <small>{session.threadId || "-"}</small>
              </div>
              <div className="col-md-3">
                <strong>User:</strong> {session.userDisplay || "Anonymous"}
                {session.acctId && (
                  <span className="ms-2 text-muted">
                    (Acct: {session.acctId})
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Interactions Grid */}
      <div ref={gridRef}>
        {isLoading && <LoadingPanel gridRef={gridRef} />}
        <Grid
          data={interactions}
          sortable={true}
          resizable={true}
          style={{ height: "calc(100vh - 400px)" }}
        >
          <Column
            field="id"
            title="ID"
            width={70}
            cells={{ data: NumericCell }}
          />
          <Column
            field="isPublic"
            title="Source"
            width={120}
            cells={{ data: SourceCell }}
          />
          <Column
            field="isSpeech"
            title="Mode"
            width={80}
            cells={{ data: ModeCell }}
          />
          <Column
            field="userMessage"
            title="User Message"
            width={250}
            cells={{ data: TruncatedCell }}
          />
          <Column
            field="aiResponse"
            title="AI Response"
            width={350}
            cells={{ data: TruncatedCell }}
          />
          <Column
            field="responseTime"
            title="Time (ms)"
            width={90}
            cells={{ data: NumericCell }}
          />
          <Column
            field="totalTokens"
            title="Tokens"
            width={80}
            cells={{ data: NumericCell }}
          />
          <Column
            field="isLiked"
            title="Liked"
            width={70}
            cells={{ data: FeedbackCell }}
          />
          <Column
            field="isReported"
            title="Reported"
            width={90}
            cells={{ data: FeedbackCell }}
          />
          <Column
            field="createdOn"
            title="Created"
            width={150}
            cells={{ data: DateCell }}
          />
        </Grid>
      </div>

      <div className="mt-3">
        <small className="text-muted">
          Click on a row to view full interaction details. Reported interactions
          are highlighted in red.
        </small>
      </div>

      {/* Interaction Detail Dialog */}
      {showDialog && selectedInteraction && (
        <Dialog
          title={`Interaction #${selectedInteraction.id}`}
          onClose={closeDialog}
          width={900}
        >
          <div className="p-3">
            {/* Source & Mode Info */}
            <div className="row mb-3">
              <div className="col-md-3">
                <strong>Source:</strong>{" "}
                {session && isDevIP(session.fromIP) ? (
                  <span className="badge bg-warning text-dark">Developer</span>
                ) : selectedInteraction.isPublic ? (
                  <span className="badge bg-info">Public</span>
                ) : (
                  <>
                    <span className="badge bg-primary">Private</span>
                    {session?.acctId && (
                      <span className="ms-2 text-muted">
                        (AcctId: {session.acctId})
                      </span>
                    )}
                  </>
                )}
              </div>
              <div className="col-md-3">
                <strong>Mode:</strong>{" "}
                {selectedInteraction.isSpeech ? "🎤 Voice" : "💬 Text"}
              </div>
              <div className="col-md-3">
                <strong>User ID:</strong> {session?.userId || "Anonymous"}
              </div>
              <div className="col-md-3">
                <strong>IP Address:</strong> {session?.fromIP || "-"}
              </div>
            </div>
            {/* Metadata */}
            <div className="row mb-3">
              <div className="col-md-3">
                <strong>Response Time:</strong>{" "}
                {selectedInteraction.responseTime
                  ? `${selectedInteraction.responseTime}ms`
                  : "-"}
              </div>
              <div className="col-md-3">
                <strong>Prompt Tokens:</strong>{" "}
                {selectedInteraction.promptTokens?.toLocaleString() || "-"}
              </div>
              <div className="col-md-3">
                <strong>Completion Tokens:</strong>{" "}
                {selectedInteraction.completionTokens?.toLocaleString() || "-"}
              </div>
              <div className="col-md-3">
                <strong>Total Tokens:</strong>{" "}
                {selectedInteraction.totalTokens?.toLocaleString() || "-"}
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-md-4">
                <strong>Created:</strong>{" "}
                {new Date(selectedInteraction.createdOn).toLocaleString()}
              </div>
              <div className="col-md-4">
                <strong>Liked:</strong>{" "}
                {selectedInteraction.isLiked === true
                  ? "👍 Yes"
                  : selectedInteraction.isLiked === false
                    ? "👎 No"
                    : "-"}
              </div>
              <div className="col-md-4">
                <strong>Reported:</strong>{" "}
                <span
                  style={{
                    color:
                      selectedInteraction.isReported === true
                        ? "#c62828"
                        : undefined,
                    fontWeight:
                      selectedInteraction.isReported === true
                        ? "bold"
                        : undefined,
                  }}
                >
                  {selectedInteraction.isReported === true ? "⚠️ Yes" : "No"}
                </span>
              </div>
            </div>

            {/* Report Message if reported */}
            {selectedInteraction.isReported &&
              selectedInteraction.reportMessage && (
                <div className="alert alert-warning mb-3">
                  <strong>Report Message:</strong>{" "}
                  {selectedInteraction.reportMessage}
                </div>
              )}

            {/* User Message */}
            <div className="mb-3">
              <h6>User Message</h6>
              <div
                className="border rounded p-3 bg-light"
                style={{
                  maxHeight: "200px",
                  overflowY: "auto",
                  whiteSpace: "pre-wrap",
                }}
              >
                {selectedInteraction.userMessage || "-"}
              </div>
            </div>

            {/* AI Response */}
            <div className="mb-3">
              <h6>AI Response</h6>
              <div
                className="border rounded p-3"
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                  whiteSpace: "pre-wrap",
                }}
              >
                {selectedInteraction.aiResponse || "-"}
              </div>
            </div>

            {/* Interaction ID */}
            {selectedInteraction.interactionId && (
              <div className="text-muted">
                <small>
                  <strong>Interaction ID:</strong>{" "}
                  {selectedInteraction.interactionId}
                </small>
              </div>
            )}
          </div>
          <DialogActionsBar>
            <button className="btn btn-secondary" onClick={closeDialog}>
              Close
            </button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

export default AIInteractions;
